/**
 * Generates a lightweight SVG data URI to be used as a placeholder image.
 * This avoids external requests to services like placehold.co, preventing ad-blocker issues.
 * 
 * @param {number|string} width - Width of the placeholder (e.g., 100, "100%")
 * @param {number|string} height - Height of the placeholder
 * @param {string} text - Text to display in the center (default: dimensions)
 * @param {string} bgColor - Background color hex (default: #EEEEEE)
 * @param {string} textColor - Text color hex (default: #333333)
 * @param {string|number} fontSize - Optional font size. If omitted, calculated automatically (15% of min dim).
 * @returns {string} Data URI string (data:image/svg+xml...)
 */
export const createPlaceholderUrl = (width = 100, height = 100, text = '', bgColor = '#EEEEEE', textColor = '#333333', fontSize = null) => {
    // If no text provided, use dimensions? Or keep empty? User logic in code used .substring(0,2) or '??'.
    // Let's use the provided text or default to clear.
    const displayText = text || `${width}x${height}`;

    // Auto-calculate font size if not provided
    let finalFontSize = fontSize;
    if (!finalFontSize) {
        const numW = parseFloat(width);
        const numH = parseFloat(height);
        // If dimensions are numbers, use 15% of the smaller dimension. Default 20.
        if (!isNaN(numW) && !isNaN(numH)) {
            finalFontSize = Math.min(numW, numH) * 0.15;
            // Floor clamp to sensible min (e.g. 10px) to avoid microscopic text
            if (finalFontSize < 10) finalFontSize = 10;
        } else {
            finalFontSize = 20;
        }
    }

    // Simple SVG template
    const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="${bgColor}"/>
        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="${finalFontSize}" font-weight="bold" fill="${textColor}" text-anchor="middle" dy=".3em">
            ${displayText}
        </text>
    </svg>
    `.trim();

    // URL encode the SVG (better than base64 for readability/debug, works in most modern browsers)
    // We must replace # with %23 for data URIs
    const encodedSvg = encodeURIComponent(svg)
        .replace(/'/g, '%27')
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29');

    return `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
};

/**
 * Returns a React style object for a placeholder container (if not using img tag)
 * @param {string} width 
 * @param {string} height 
 */
export const getPlaceholderStyles = (width = '100%', height = '100%') => ({
    width: width,
    height: height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EEEEEE',
    color: '#333333',
    fontWeight: 'bold',
    fontSize: '14px',
    userSelect: 'none'
});
